import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { CURRENCIES, TIMEZONES, CONSENT_LANGS } from "../lib/helpers";
import { PageHeader, Field, NativeSelect } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEK = Object.fromEntries(DAYS.map((_, i) => [String(i), { open: "09:00", close: "19:00", closed: i === 6 }]));
const BLANK = { name: "", address: "", phone: "", email: "", currency: "EUR", timezone: "Europe/Rome", language: "en", opening_hours: WEEK, closed_dates: [], active: true };

function LocationEditor({ open, onOpenChange, location, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState(BLANK);
  const [closed, setClosed] = useState("");
  useEffect(() => {
    if (open) { setF({ ...BLANK, ...(location || {}), opening_hours: { ...WEEK, ...(location?.opening_hours || {}) } }); setClosed((location?.closed_dates || []).join(", ")); }
  }, [open, location]);
  const setDay = (d, k, v) => setF({ ...f, opening_hours: { ...f.opening_hours, [d]: { ...f.opening_hours[d], [k]: v } } });

  const save = async () => {
    const body = { ...f, closed_dates: closed.split(",").map((s) => s.trim()).filter(Boolean) };
    delete body.id; delete body.created_at;
    try {
      location?.id ? await api.put(`/locations/${location.id}`, body) : await api.post("/locations", body);
      toast.success("Location saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="location-editor">
        <DialogHeader><DialogTitle>{t("location")}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {["name", "address", "phone", "email"].map((k) => (
            <Field key={k} label={t(k)}><Input data-testid={`location-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
          ))}
          <Field label={t("currency")}><NativeSelect testId="location-currency-select" value={f.currency} onChange={(v) => setF({ ...f, currency: v })} options={CURRENCIES.map((c) => ({ value: c, label: c }))} /></Field>
          <Field label={t("timezone")}><NativeSelect testId="location-timezone-select" value={f.timezone} onChange={(v) => setF({ ...f, timezone: v })} options={TIMEZONES.map((c) => ({ value: c, label: c }))} /></Field>
          <Field label={t("language")}><NativeSelect testId="location-language-select" value={f.language} onChange={(v) => setF({ ...f, language: v })} options={Object.entries(CONSENT_LANGS).map(([v, l]) => ({ value: v, label: l }))} /></Field>
          <label className="flex items-center gap-3 text-sm pt-6"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="location-active-switch" />{t("active")}</label>
          <div className="sm:col-span-2 rounded-[var(--radius)] border p-4 space-y-2">
            <p className="text-sm font-semibold mb-2">{t("opening_hours")}</p>
            {DAYS.map((d, i) => {
              const h = f.opening_hours[String(i)];
              return (
                <div key={d} className="grid grid-cols-[48px_1fr_1fr_auto] items-center gap-3 text-sm">
                  <span className="font-medium">{d}</span>
                  <Input type="time" disabled={h.closed} value={h.open} onChange={(e) => setDay(String(i), "open", e.target.value)} data-testid={`location-open-${i}`} />
                  <Input type="time" disabled={h.closed} value={h.close} onChange={(e) => setDay(String(i), "close", e.target.value)} data-testid={`location-close-${i}`} />
                  <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={h.closed} onCheckedChange={(v) => setDay(String(i), "closed", !!v)} data-testid={`location-closed-${i}`} />{t("closed")}</label>
                </div>
              );
            })}
          </div>
          <Field label={`${t("closed")} (YYYY-MM-DD, …)`} className="sm:col-span-2"><Input data-testid="location-closed-dates" value={closed} onChange={(e) => setClosed(e.target.value)} placeholder="2026-12-25, 2026-12-26" /></Field>
        </div>
        <Button className="rounded-full mt-2" data-testid="location-save-button" disabled={!f.name} onClick={save}>{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Locations() {
  const { locations, reload } = useApp();
  const { t } = useT();
  const [edit, setEdit] = useState(null);
  return (
    <div>
      <PageHeader title={t("locations")}><Button className="rounded-full" data-testid="new-location-btn" onClick={() => setEdit({})}>{t("create")}</Button></PageHeader>
      <div className="grid gap-4 md:grid-cols-2 stagger">
        {locations.map((l) => (
          <button key={l.id} onClick={() => setEdit(l)} data-testid={`location-card-${l.id}`} className={`lift text-left rounded-[var(--radius)] border bg-card p-6 ${l.active ? "" : "opacity-50"}`}>
            <div className="flex items-start justify-between">
              <div><MapPin className="h-5 w-5 text-primary mb-3" /><div className="font-display text-xl font-semibold">{l.name}</div>
                <div className="text-sm text-muted-foreground">{l.address}</div></div>
              <span className="font-mono text-2xl font-bold text-[hsl(var(--brand-accent))]">{l.currency}</span>
            </div>
            <div className="mt-4 flex gap-4 text-xs text-muted-foreground"><span>{l.timezone}</span><span>{CONSENT_LANGS[l.language]}</span></div>
          </button>
        ))}
      </div>
      <LocationEditor open={!!edit} onOpenChange={(o) => !o && setEdit(null)} location={edit} onSaved={reload} />
    </div>
  );
}
